import React from 'react';
import { X, CheckCircle } from 'lucide-react';
import type { Campsite } from '@/types/campsite';

interface CampsiteDetailProps {
  campsite: Campsite;
  onClose: () => void;
}

const CampsiteDetail: React.FC<CampsiteDetailProps> = ({ campsite, onClose }) => {
  const getOccupancyColor = (percentage: number) => {
    if (percentage >= 90) return '#F53743';
    if (percentage >= 70) return '#FFB230';
    if (percentage >= 50) return '#FFB230';
    return '#719C40';
  };

  const getOccupancyLabel = (percentage: number) => {
    if (percentage >= 90) return 'Getting Crowded';
    if (percentage >= 70) return 'Comfortably Busy';
    if (percentage >= 50) return 'Plenty of Space';
    return 'Plenty of Space';
  };

  return (
    <div className="w-full bg-white p-6 relative">
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-all z-10"
      >
        <X className="w-5 h-5" />
      </button>

      <div className="flex gap-6">
        {/* Left: Image and Description */}
        <div className="w-[350px] flex-shrink-0">
          {/* Image with carousel dots */}
          <div className="relative h-48 bg-gray-100 rounded-xl overflow-hidden mb-4">
            {campsite.images.length > 0 && (
              <img
                src={campsite.images[0]}
                alt={campsite.name}
                className="w-full h-full object-cover"
              />
            )}
            {/* Carousel dots */}
            <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex gap-1">
              {campsite.images.map((_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full ${index === 0 ? 'bg-blue-500' : 'bg-white/50'}`}
                />
              ))}
            </div>
          </div>

          {/* Description */}
          <p
            style={{
              fontFamily: 'Inter',
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '20px',
              color: '#667479'
            }}
          >
            {campsite.description}
          </p>
        </div>

        {/* Right: Details - Two columns with borders */}
        <div className="flex-1">
          {/* Header */}
          <div className="mb-4">
            <div
              className="mb-1"
              style={{
                fontFamily: 'Inter',
                fontSize: '12px',
                fontWeight: 400,
                color: '#667479'
              }}
            >
              Ex PSL/B/302
            </div>
            <h2
              className="mb-1"
              style={{
                fontFamily: 'Poppins',
                fontSize: '24px',
                fontWeight: 700,
                lineHeight: '32px',
                color: '#000000'
              }}
            >
              {campsite.name}
            </h2>
            <p
              style={{
                fontFamily: 'Inter',
                fontSize: '16px',
                fontWeight: 500,
                color: '#000000'
              }}
            >
              Hok Tau Reservoir Barbecue Area Site 1
            </p>
          </div>

          {/* Info Rows */}
          <div>
            <InfoRow label="Location" value={`${campsite.location}, New Territories`} />
            <InfoRow label="Country Park" value="Pat Sin Leng Country Park (八仙嶺郊野公園)" />
            <InfoRow
              label="Coordinates / Map Link"
              value="View on Map ( (22.505° N, 114.167° E)"
              link
            />
            <InfoRow label="Type" value="BBQ Site" />
            <InfoRow label="Number of BBQ Pits" value="Less than 10" />

            {/* Nearby Facilities */}
            <div className="flex py-4 border-b border-gray-200">
              <div className="w-1/3">
                <span style={{ fontFamily: 'Inter', fontSize: '14px', fontWeight: 400, color: '#667479' }}>
                  Nearby Facilities
                </span>
              </div>
              <div className="flex-1">
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  <div className="flex items-center gap-1">
                    <span style={{ fontFamily: 'Inter', fontSize: '14px', fontWeight: 400, color: '#000000' }}>
                      Restrooms
                    </span>
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  </div>
                  <div className="flex items-center gap-1">
                    <span style={{ fontFamily: 'Inter', fontSize: '14px', fontWeight: 400, color: '#000000' }}>
                      Picnic Tables
                    </span>
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  </div>
                  <div className="flex items-center gap-1">
                    <span style={{ fontFamily: 'Inter', fontSize: '14px', fontWeight: 400, color: '#000000' }}>
                      Waste Bins
                    </span>
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  </div>
                </div>
              </div>
            </div>

            <InfoRow
              label="Access / Transportation"
              value="Accessible by bus or minibus from Fanling / Tai Po; 20-min walk from trailhead"
            />

            {/* Occupancy with colored dot */}
            <div className="flex py-4 border-b border-gray-200">
              <div className="w-1/3">
                <span style={{ fontFamily: 'Inter', fontSize: '14px', fontWeight: 400, color: '#667479' }}>
                  Occupancy (Real-time)
                </span>
              </div>
              <div className="flex-1 flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: getOccupancyColor(campsite.occupancy.percentage) }}
                />
                <span style={{ fontFamily: 'Inter', fontSize: '14px', fontWeight: 400, color: '#000000' }}>
                  {getOccupancyLabel(campsite.occupancy.percentage)}
                </span>
              </div>
            </div>

            <InfoRow label="Air Quality (PM2.5)" value="35 µg/m³ — Moderate" />
            <InfoRow label="Temperature / Humidity" value="27°C / 82%" />
            <InfoRow label="Safety Alerts (AI)" value="None (Last updated: 15:14 - 8 Oct 2025)" />
            <InfoRow
              label="Managed by"
              value="AFCD (Agriculture, Fisheries and Conservation Department)"
            />
            <InfoRow label="Hotline" value="1823" />
            <InfoRow label="Website" value="AFCD Official Campsite Info" link />
            <InfoRow
              label="Smart Monitoring System"
              value="A-IoT Solar Device installed (by Alpha AI Technology)"
            />

            {/* Notes */}
            <div className="flex py-4">
              <div className="w-1/3">
                <span style={{ fontFamily: 'Inter', fontSize: '14px', fontWeight: 400, color: '#667479' }}>
                  #Notes
                </span>
              </div>
              <div className="flex-1">
                <p
                  className="italic"
                  style={{
                    fontFamily: 'Inter',
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '20px',
                    color: '#667479'
                  }}
                >
                  AI-powered camera system tested for visitor counting and environmental data
                  collection; no personal data recorded per PCPD guidelines.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const InfoRow: React.FC<{
  label: string;
  value: string;
  link?: boolean;
}> = ({ label, value, link }) => (
  <div className="flex py-4 border-b border-gray-200">
    <div className="w-1/3">
      <span style={{ fontFamily: 'Inter', fontSize: '14px', fontWeight: 400, color: '#667479' }}>
        {label}
      </span>
    </div>
    <div className="flex-1">
      <span
        className={link ? 'underline' : ''}
        style={{
          fontFamily: 'Inter',
          fontSize: '14px',
          fontWeight: 400,
          color: link ? '#3B82F6' : '#000000'
        }}
      >
        {value}
      </span>
    </div>
  </div>
);

export default CampsiteDetail;
